import localeEn from "../locales/countries.en.json";
import localeFi from "../locales/countries.fi.json";
import localeSe from "../locales/countries.se.json";
import localeNo from "../locales/countries.no.json";
import useI18n from "./useI18n";

const locales = {
    en: localeEn,
    fi: localeFi,
    se: localeSe,
    no: localeNo
}

const nordicCountries = ['FI', 'SE', 'NO', 'DK', 'IS'];
const europeanCountries = ['AL', 'AD', 'AT', 'BY', 'BE', 'BA', 'BG', 'HR', 'CY', 'CZ', 'EE', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'XK', 'LV', 'LI', 'LT', 'LU', 'MT', 'MD', 'MC', 'ME', 'NL', 'MK', 'PL', 'PT', 'RO', 'RU', 'SM', 'RS', 'SK', 'SI', 'ES', 'CH', 'UA', 'GB', 'VA'];
const restOfTheWorldCountries = ['AF', 'AO', 'AR', 'AM', 'AU', 'AZ', 'BH', 'BD', 'BB', 'BZ', 'BJ', 'BT', 'BO', 'BW', 'BR', 'BN', 'BF', 'BI', 'KH', 'CM', 'CA', 'CV', 'CF', 'TD', 'CL', 'CN', 'CO', 'KM', 'CG', 'CD', 'CR', 'CI', 'CU', 'DJ', 'DM', 'DO', 'EC', 'EG', 'SV', 'GQ', 'ER', 'SZ', 'ET', 'FJ', 'GA', 'GM', 'GE', 'GH', 'GD', 'GT', 'GN', 'GW', 'GY', 'HT', 'HN', 'IN', 'ID', 'IR', 'IQ', 'IL', 'JM', 'JP', 'JO', 'KZ', 'KE', 'KI', 'KP', 'KR', 'KW', 'KG', 'LA', 'LB', 'LS', 'LR', 'LY', 'MG', 'MW', 'MY', 'MV', 'ML', 'MH', 'MR', 'MU', 'MX', 'FM', 'MN', 'MA', 'MZ', 'MM', 'NA', 'NR', 'NP', 'NZ', 'NI', 'NE', 'NG', 'OM', 'PK', 'PW', 'PS', 'PA', 'PG', 'PY', 'PE', 'PH', 'QA', 'RW', 'KN', 'LC', 'VC', 'WS', 'ST', 'SA', 'SN', 'SC', 'SL', 'SG', 'SB', 'SO', 'ZA', 'SS', 'LK', 'SD', 'SR', 'SY', 'TJ', 'TZ', 'TH', 'TL', 'TG', 'TO', 'TT', 'TN', 'TR', 'TM', 'TV', 'UG', 'AE', 'US', 'UY', 'UZ', 'VU', 'VE', 'VN', 'YE', 'ZM', 'ZW'];

const localeToCountryMap = {
    'fi': 'FI',
    'se': 'SE',
    'no': 'NO'
}

const useCountries = () => {
    const { getLocale, getCountry } = useI18n();

    /**
     * Translate country code to localized country name using the current locale.
     * @param {string} code
     * @returns {string}
     */
    const translateCountryCode = (code) => {
        const locale = getLocale() ?? 'en';
        const localizedCountries = locales[locale] ?? locales['en'];
        return localizedCountries[code] ?? code;
    }

    /**
     * Sort countries alphabetically by their localized names.
     * @param {string} countryCodeA
     * @param {string} countryCodeB
     * @returns {number}
     */
    const sortCountries = (countryCodeA, countryCodeB) => {
        const nameA = translateCountryCode(countryCodeA);
        const nameB = translateCountryCode(countryCodeB);
        return nameA.localeCompare(nameB);
    }

    /**
     * Get the list of countries with localized names, with the current country at the top of the list.
     * @returns {{code: string, name: string}[]}
     */
    const getCountries = () => {
        nordicCountries.sort(sortCountries);
        europeanCountries.sort(sortCountries);
        restOfTheWorldCountries.sort(sortCountries);
        const countries = [
            ...nordicCountries,
            ...europeanCountries,
            ...restOfTheWorldCountries
        ];
        // Set current country to the top of the list
        const currentCountry = localeToCountryMap[getCountry().toLowerCase()] ?? 'FI';
        countries.sort((a, b) => {
            if (a === currentCountry) { return -1; }
            if (b === currentCountry) { return 1; }
            return 0;
        });
        return countries.map(code => ({ code, name: translateCountryCode(code) ?? code }));
    }

    return {
        getCountries
    }
}

export default useCountries;
